import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Crown, Star, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { useSubscription } from '../contexts/SubscriptionContext';

/**
 * ModuleGatedPage - Wrapper component for pages that require specific tier access
 * 
 * Usage in App.js routes:
 * <ModuleGatedPage module="board_packs" requiredTier="Pro">
 *   <BoardPackPage />
 * </ModuleGatedPage>
 */
export const ModuleGatedPage = ({ 
  module, 
  requiredTier = 'Pro',
  children 
}) => {
  const { hasModuleAccess, tierDisplayName, loading, isOnTrial } = useSubscription();
  
  // During loading, show children (assume access)
  if (loading) {
    return <>{children}</>;
  }
  
  // During trial, all modules are accessible
  if (isOnTrial) {
    return <>{children}</>;
  }
  
  // Check actual module access
  const hasAccess = hasModuleAccess(module);
  
  if (hasAccess) {
    return <>{children}</>;
  }

  // Get module display name
  const moduleNames = {
    board_packs: 'Board Packs',
    board_management: 'Board Management',
    pmo: 'PMO',
    contracts: 'Contract Management',
    ma: 'M&A',
    ma_plus: 'M&A Pro+',
    deal_room: 'Deal Room',
    due_diligence_workflows: 'Due Diligence Workflows',
    integration_playbooks: 'Integration Playbooks',
    cosec: 'Company Secretary'
  };

  const moduleName = moduleNames[module] || module;

  // Show upgrade page
  return (
    <main className="relative max-w-[90rem] mx-auto px-6 lg:px-12 pt-6 pb-8">
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-lg w-full border-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm">
          <CardContent className="pt-12 pb-8 px-8 text-center">
            {/* Lock Icon */}
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Lock className="h-10 w-10 text-primary" />
            </div>
            
            {/* Title */}
            <h1 className="text-2xl font-bold text-white mb-2">
              {moduleName} is a {requiredTier} Feature
            </h1>
            
            <div className="text-gray-400 mb-6">
              You're currently on the <Badge variant="secondary" className="mx-1">{tierDisplayName}</Badge> plan.
              Upgrade to <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mx-1">{requiredTier}</Badge> 
              to unlock {moduleName} and supercharge your operations.
            </div>

            {/* Features Preview */}
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                What you'll unlock with {requiredTier}:
              </h3>
              <ul className="space-y-2 text-sm text-gray-300">
                {requiredTier === 'Pro' ? (
                  <>
                    <li className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-400" /> Board Packs & Board Management
                    </li>
                    <li className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-400" /> PMO with Project Tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-400" /> Contract Management
                    </li>
                    <li className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-400" /> M&A Deal Tracking
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-violet-400" /> Deal Room Management
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-violet-400" /> Due Diligence Workflows
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-violet-400" /> Integration Playbooks
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-violet-400" /> Priority Support
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700">
                <Link to="/pricing">
                  <Crown className="h-4 w-4 mr-2" />
                  Upgrade to {requiredTier}
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-slate-600 hover:bg-slate-800">
                <Link to="/pricing">
                  Compare Plans <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </div>

            {/* Founding Member Note */}
            <p className="text-xs text-amber-400 mt-4 flex items-center justify-center gap-1">
              <Star className="h-3 w-3" />
              Founding Members get 50% off - Limited spots available
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default ModuleGatedPage;
